/**
 * RevenueSharingConfigPage — `/settings/revenue-sharing`.
 *
 * Versioned, configurable revenue split rules per service / tier / channel.
 * Backed by the generic settings-collection store on service-audit
 * (`GET / PUT /v1/settings-collections/revenue_share_rules`).
 *
 * CRUD model:
 *   - Each rule lives in local React state. The top-level "Save changes"
 *     button replaces the whole collection via PUT.
 *   - Per-row Edit opens a drawer that mutates the in-memory rule;
 *     drawer-Save updates state + marks dirty (no network call yet).
 *   - Per-row Delete removes from state behind a confirm modal.
 */
import { type JSX, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Save, Trash2, X } from 'lucide-react';

import {
  getSettingsCollection,
  putSettingsCollection,
} from '../../lib/api/settings';
import { PageHeader, StatusBadge } from '../shared/StaffUI';

interface SplitLine {
  readonly id: string;
  readonly recipient_type: string;
  readonly percentage: number | null;
  readonly is_residual: boolean;
  readonly recipient_account_ref?: string | null;
}

interface Rule {
  readonly rule_id: string;
  readonly name: string;
  readonly service_type: string;
  readonly tier: string;
  readonly channel: string;
  readonly effective_from: string;
  readonly status: 'PUBLISHED' | 'DRAFT' | 'SUPERSEDED';
  readonly version: number;
  readonly lines: readonly SplitLine[];
}

const COLLECTION_KEY = 'revenue_share_rules' as const;

const SERVICE_TYPES = ['ALL', 'MAIL', 'PARCEL', 'EMS', 'REGISTERED_MAIL'] as const;
const TIERS = ['ALL', 'TIER_1', 'TIER_2', 'TIER_3'] as const;
const CHANNELS = ['ALL', 'COUNTER', 'WEB', 'API', 'MOBILE'] as const;
const STATUSES = ['DRAFT', 'PUBLISHED'] as const;
const RECIPIENT_TYPES = [
  'NIPOST_MASTER',
  'FRANCHISE_OPERATOR',
  'PARTNER',
  'AGENT',
  'PLATFORM_FEE',
] as const;

export function RevenueSharingConfigPage(): JSX.Element {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['settings-collection', COLLECTION_KEY],
    queryFn: () => getSettingsCollection<Rule>(COLLECTION_KEY),
    retry: false,
    staleTime: 30_000,
  });

  const [rules, setRules] = useState<readonly Rule[]>([]);
  const [dirty, setDirty] = useState(false);
  const [editing, setEditing] = useState<Rule | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Rule | null>(null);

  useEffect(() => {
    if (query.data && !dirty) {
      setRules(query.data.items);
    }
  }, [query.data, dirty]);

  const saveMut = useMutation({
    mutationFn: (next: readonly Rule[]) =>
      putSettingsCollection<Rule>(COLLECTION_KEY, next),
    onSuccess: async () => {
      setDirty(false);
      await qc.invalidateQueries({ queryKey: ['settings-collection', COLLECTION_KEY] });
    },
  });

  const published = rules.filter((r) => r.status === 'PUBLISHED').length;
  const drafts = rules.filter((r) => r.status === 'DRAFT').length;

  function newRule(): void {
    const id = `rule-${Date.now()}`;
    const blank: Rule = {
      rule_id: id,
      name: 'New rule',
      service_type: 'ALL', tier: 'ALL', channel: 'ALL',
      effective_from: new Date().toISOString().slice(0, 10),
      status: 'DRAFT', version: 1,
      lines: [{ id: 'l1', recipient_type: 'NIPOST_MASTER', percentage: null, is_residual: true }],
    };
    setRules([blank, ...rules]);
    setDirty(true);
  }

  function deleteRule(id: string): void {
    setRules(rules.filter((r) => r.rule_id !== id));
    setDirty(true);
    setConfirmDelete(null);
  }

  function commitEdit(next: Rule): void {
    setRules(rules.map((r) => (r.rule_id === next.rule_id ? next : r)));
    setDirty(true);
    setEditing(null);
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Revenue share rules"
        description="Versioned, configurable splits per service type and channel."
        actions={
          <div className="flex items-center gap-2">
            {dirty ? (
              <button
                type="button"
                onClick={() => saveMut.mutate(rules)}
                disabled={saveMut.isPending}
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-nipost-green text-white rounded-lg text-sm font-medium disabled:opacity-60"
              >
                <Save className="w-4 h-4" /> {saveMut.isPending ? 'Saving…' : 'Save changes'}
              </button>
            ) : null}
            <button
              type="button"
              onClick={newRule}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-nipost-green text-white rounded-lg text-sm font-medium"
            >
              <Plus className="w-4 h-4" /> New rule
            </button>
          </div>
        }
      />

      {query.isPending ? (
        <p className="text-sm text-muted">Loading rules…</p>
      ) : query.isError ? (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-900">
          Could not load revenue share rules: {(query.error as Error).message}
        </div>
      ) : null}

      {saveMut.isError ? (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-900">
          Save failed: {(saveMut.error as Error).message}
        </div>
      ) : null}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white border border-border rounded-xl p-4">
          <p className="text-[10px] font-bold text-muted uppercase tracking-wider">Active rules</p>
          <p className="text-2xl font-bold text-nipost-green mt-1">{published}</p>
        </div>
        <div className="bg-white border border-border rounded-xl p-4">
          <p className="text-[10px] font-bold text-muted uppercase tracking-wider">Drafts</p>
          <p className="text-2xl font-bold text-amber-600 mt-1">{drafts}</p>
        </div>
        <div className="bg-white border border-border rounded-xl p-4">
          <p className="text-[10px] font-bold text-muted uppercase tracking-wider">Last updated</p>
          <p className="text-2xl font-bold text-gray-700 mt-1">
            {query.data?.updatedAt
              ? new Date(query.data.updatedAt).toISOString().slice(0, 10)
              : '—'}
          </p>
        </div>
        <div className="bg-white border border-border rounded-xl p-4">
          <p className="text-[10px] font-bold text-muted uppercase tracking-wider">Total rules</p>
          <p className="text-2xl font-bold text-gray-700 mt-1">{rules.length}</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-[10px] text-muted uppercase tracking-wider">
            <tr>
              <th className="p-3 text-left">Rule</th>
              <th className="p-3 text-left">Service</th>
              <th className="p-3 text-left">Tier</th>
              <th className="p-3 text-left">Channel</th>
              <th className="p-3 text-left">Effective</th>
              <th className="p-3 text-center">Lines</th>
              <th className="p-3 text-center">Status</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rules.map((r) => (
              <tr key={r.rule_id} className="border-t border-border hover:bg-gray-50">
                <td className="p-3">
                  <p className="font-semibold text-gray-900">{r.name}</p>
                  <p className="text-[10px] text-muted">v{r.version}</p>
                </td>
                <td className="p-3 font-mono text-xs">{r.service_type}</td>
                <td className="p-3 font-mono text-xs">{r.tier}</td>
                <td className="p-3 font-mono text-xs">{r.channel}</td>
                <td className="p-3 text-xs">{r.effective_from}</td>
                <td className="p-3 text-center text-xs">{r.lines.length}</td>
                <td className="p-3 text-center"><StatusBadge status={r.status} /></td>
                <td className="p-3 text-right whitespace-nowrap">
                  <button
                    type="button"
                    aria-label={`Edit ${r.name}`}
                    onClick={() => setEditing(r)}
                    className="text-gray-600 hover:bg-gray-100 rounded p-1 mr-1"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    aria-label={`Delete ${r.name}`}
                    onClick={() => setConfirmDelete(r)}
                    className="text-red-600 hover:bg-red-50 rounded p-1"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing ? (
        <RuleEditorDrawer
          rule={editing}
          onCancel={() => setEditing(null)}
          onSave={commitEdit}
        />
      ) : null}

      {confirmDelete ? (
        <ConfirmDeleteModal
          rule={confirmDelete}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => deleteRule(confirmDelete.rule_id)}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// RuleEditorDrawer — full editor for one rule (top fields + lines array).
// ---------------------------------------------------------------------------

interface RuleEditorDrawerProps {
  readonly rule: Rule;
  readonly onCancel: () => void;
  readonly onSave: (next: Rule) => void;
}

function RuleEditorDrawer(props: RuleEditorDrawerProps): JSX.Element {
  const [draft, setDraft] = useState<Rule>(props.rule);
  const [error, setError] = useState<string | null>(null);

  function setField<K extends keyof Rule>(key: K, value: Rule[K]): void {
    setDraft({ ...draft, [key]: value });
  }

  function setLine(idx: number, next: SplitLine): void {
    setDraft({
      ...draft,
      lines: draft.lines.map((l, i) => (i === idx ? next : l)),
    });
  }

  function addLine(): void {
    setDraft({
      ...draft,
      lines: [
        ...draft.lines,
        {
          id: `l${draft.lines.length + 1}-${Date.now()}`,
          recipient_type: 'PARTNER',
          percentage: 0,
          is_residual: false,
        },
      ],
    });
  }

  function removeLine(idx: number): void {
    setDraft({ ...draft, lines: draft.lines.filter((_, i) => i !== idx) });
  }

  function validate(): string | null {
    // Percentages sum to <= 100 (residual lines excluded — they take the
    // leftover).
    const explicit = draft.lines.filter((l) => !l.is_residual);
    // eslint-disable-next-line upmp/no-number-money -- percentage points, not money
    const sum = explicit.reduce((acc, l) => acc + (l.percentage ?? 0), 0);
    if (sum > 100) return `Explicit percentages sum to ${sum}; must be ≤ 100.`;
    const hasResidual = draft.lines.some((l) => l.is_residual);
    if (hasResidual) {
      const nonResidualMissing = draft.lines.some(
        (l) => !l.is_residual && (l.percentage === null || l.percentage === undefined),
      );
      if (nonResidualMissing) {
        return 'When a residual line is present, every other line must declare an explicit percentage.';
      }
    }
    return null;
  }

  function submit(): void {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    props.onSave(draft);
  }

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-stretch justify-end">
      <div className="bg-white w-full max-w-xl h-full overflow-y-auto border-l border-border">
        <div className="flex items-center justify-between p-4 border-b border-border sticky top-0 bg-white">
          <p className="text-base font-bold text-gray-900">Edit rule</p>
          <button
            type="button"
            onClick={props.onCancel}
            className="p-1.5 rounded hover:bg-gray-100"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <label htmlFor="rule-name" className="text-[10px] font-semibold uppercase text-muted">Name</label>
            <input
              id="rule-name"
              type="text"
              aria-label="Rule name"
              value={draft.name}
              onChange={(e) => setField('name', e.target.value)}
              className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="rule-service" className="text-[10px] font-semibold uppercase text-muted">Service type</label>
              <select
                id="rule-service"
                aria-label="Service type"
                value={draft.service_type}
                onChange={(e) => setField('service_type', e.target.value)}
                className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg"
              >
                {SERVICE_TYPES.map((s) => (<option key={s} value={s}>{s}</option>))}
              </select>
            </div>
            <div>
              <label htmlFor="rule-tier" className="text-[10px] font-semibold uppercase text-muted">Tier</label>
              <select
                id="rule-tier"
                aria-label="Tier"
                value={draft.tier}
                onChange={(e) => setField('tier', e.target.value)}
                className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg"
              >
                {TIERS.map((s) => (<option key={s} value={s}>{s}</option>))}
              </select>
            </div>
            <div>
              <label htmlFor="rule-channel" className="text-[10px] font-semibold uppercase text-muted">Channel</label>
              <select
                id="rule-channel"
                aria-label="Channel"
                value={draft.channel}
                onChange={(e) => setField('channel', e.target.value)}
                className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg"
              >
                {CHANNELS.map((s) => (<option key={s} value={s}>{s}</option>))}
              </select>
            </div>
            <div>
              <label htmlFor="rule-status" className="text-[10px] font-semibold uppercase text-muted">Status</label>
              <select
                id="rule-status"
                aria-label="Status"
                value={draft.status === 'SUPERSEDED' ? 'DRAFT' : draft.status}
                onChange={(e) => setField('status', e.target.value as Rule['status'])}
                className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg"
              >
                {STATUSES.map((s) => (<option key={s} value={s}>{s}</option>))}
              </select>
            </div>
            <div className="col-span-2">
              <label htmlFor="rule-effective" className="text-[10px] font-semibold uppercase text-muted">Effective from</label>
              <input
                id="rule-effective"
                type="date"
                aria-label="Effective from"
                value={draft.effective_from}
                onChange={(e) => setField('effective_from', e.target.value)}
                className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg"
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-[10px] font-semibold uppercase text-muted">Split lines</p>
              <button
                type="button"
                onClick={addLine}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-border rounded hover:bg-gray-50"
              >
                <Plus className="w-3 h-3" /> Add line
              </button>
            </div>

            {draft.lines.map((line, idx) => (
              <div key={line.id} className="grid grid-cols-12 gap-2 p-2 border border-border rounded-lg items-end">
                <div className="col-span-4">
                  <label className="text-[10px] text-muted">Recipient type</label>
                  <select
                    aria-label={`Recipient type line ${idx + 1}`}
                    value={line.recipient_type}
                    onChange={(e) =>
                      setLine(idx, { ...line, recipient_type: e.target.value })
                    }
                    className="w-full mt-1 px-2 py-1.5 text-xs border border-border rounded"
                  >
                    {RECIPIENT_TYPES.map((s) => (<option key={s} value={s}>{s}</option>))}
                  </select>
                </div>
                <div className="col-span-3">
                  <label className="text-[10px] text-muted">Percentage</label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step="0.01"
                    aria-label={`Percentage line ${idx + 1}`}
                    disabled={line.is_residual}
                    value={line.percentage ?? ''}
                    onChange={(e) =>
                      setLine(idx, {
                        ...line,
                        percentage: e.target.value === '' ? null : Number(e.target.value),
                      })
                    }
                    className="w-full mt-1 px-2 py-1.5 text-xs border border-border rounded font-mono disabled:bg-gray-100"
                  />
                </div>
                <div className="col-span-3">
                  <label className="text-[10px] text-muted">Account ref</label>
                  <input
                    type="text"
                    aria-label={`Account ref line ${idx + 1}`}
                    value={line.recipient_account_ref ?? ''}
                    onChange={(e) =>
                      setLine(idx, { ...line, recipient_account_ref: e.target.value || null })
                    }
                    className="w-full mt-1 px-2 py-1.5 text-xs border border-border rounded font-mono"
                  />
                </div>
                <div className="col-span-1 flex items-center pb-1">
                  <input
                    id={`residual-${idx}`}
                    type="checkbox"
                    aria-label={`Residual line ${idx + 1}`}
                    checked={line.is_residual}
                    onChange={(e) => {
                      const isResidual = e.target.checked;
                      setLine(idx, {
                        ...line,
                        is_residual: isResidual,
                        percentage: isResidual ? null : (line.percentage ?? 0),
                      });
                    }}
                  />
                  <label htmlFor={`residual-${idx}`} className="text-[10px] text-muted ml-1">Res.</label>
                </div>
                <div className="col-span-1 flex justify-end pb-1">
                  <button
                    type="button"
                    aria-label={`Remove line ${idx + 1}`}
                    onClick={() => removeLine(idx)}
                    className="text-red-600 hover:bg-red-50 rounded p-1"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {error ? (
            <div className="text-xs text-red-600" role="alert">{error}</div>
          ) : null}
        </div>

        <div className="sticky bottom-0 flex items-center justify-end gap-2 p-4 border-t border-border bg-white">
          <button
            type="button"
            onClick={props.onCancel}
            className="px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            className="px-3 py-1.5 text-sm bg-nipost-green text-white rounded-lg"
          >
            Apply changes
          </button>
        </div>
      </div>
    </div>
  );
}

interface ConfirmDeleteModalProps {
  readonly rule: Rule;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}

function ConfirmDeleteModal(props: ConfirmDeleteModalProps): JSX.Element {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl border border-border w-full max-w-md">
        <div className="p-4 border-b border-border">
          <p className="text-base font-bold text-gray-900">Delete rule</p>
        </div>
        <div className="p-4 text-sm text-gray-700 space-y-2">
          <p>
            Delete revenue-share rule <span className="font-semibold">{props.rule.name}</span>{' '}
            ({props.rule.service_type} / {props.rule.tier} / {props.rule.channel})?
          </p>
          <p className="text-xs text-muted">
            The rule is removed from the local list. Click "Save changes" on the page header to persist the deletion.
          </p>
        </div>
        <div className="flex items-center justify-end gap-2 p-4 border-t border-border">
          <button
            type="button"
            onClick={props.onCancel}
            className="px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={props.onConfirm}
            className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg"
          >
            Delete rule
          </button>
        </div>
      </div>
    </div>
  );
}
